import React, { useState, useEffect, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import translate from '../../../../commonComponents/translate';
import Form from '../../../../commonComponents/forms/Form';
import FormFooter from '../../../../commonComponents/forms/FormFooter';
import FormSubtitle from '../../../../commonComponents/forms/FormSubtitle';
import ImageInput from '../../../../commonComponents/forms/ImageInput';
import {
  compose,
  handleImageUpload,
  PROFILE_BLOCK,
} from '../../../../services/utilityFunctions';
import withCSRF from '../../../../commonComponents/withCSRF';
import SkipStepButton from '../../../journey/overall/content/components/SkipStepButton';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import {
  checkIfHasErrors,
  getErrors,
  getValidationRules,
  isFieldRequiredLabel,
} from '../../helpers/helper';
import ErrorMessage from '../../../../commonComponents/forms/ErrorMessage';

function initialProps() {
  return {
    unhappy: { blob: null, fileName: '' },
    moodless: { blob: null, fileName: '' },
    happy: { blob: null, fileName: '' },
  };
}

/**
 * Form to update the current logged user
 */
function MoodsForm({ t, _csrf, member, onMemberChange, onSkipStep }) {
  const { organization } = useContext(ConfigsContext);
  const { requiredFieldsPerBlock = [] } =
    organization?.services?.me?.config || {};
  const [state, setState] = useState(initialProps());
  const [errors, setErrors] = useState({
    unhappy: false,
    moodless: false,
    happy: false,
  });
  const [imageTouched, setImageTouched] = useState({
    unhappy: false,
    moodless: false,
    happy: false,
  });

  useEffect(() => setState(initialProps()), [member]);

  const formId = 'moodsForm';
  const validationRules = useMemo(
    () => getValidationRules(requiredFieldsPerBlock, PROFILE_BLOCK.MOODS, {}),
    [getValidationRules, requiredFieldsPerBlock],
  );

  const imageUpload = async (name, original, onlyURL, isVideo) => {
    if (!imageTouched[name]) {
      const result = original;
      if (result && result._id) {
        delete result._id;
      }
      return result;
    }
    const result = await handleImageUpload(t, _csrf, state[name], isVideo);
    return onlyURL ? result.url : result;
  };

  const imageUploads = () => {
    const mood = member.mood || {};
    const requests = ['unhappy', 'moodless', 'happy'].map(m =>
      imageUpload(m, mood[m], true),
    );
    return Promise.all(requests);
  };

  const handleSubmit = async () => {
    const [unhappy, moodless, happy] = await imageUploads();

    const newErrors = getErrors(validationRules, {
      unhappy,
      moodless,
      happy,
    });

    if (checkIfHasErrors(newErrors)) {
      setErrors(newErrors);
      return;
    }
    const params = {
      mood: { unhappy, moodless, happy },
    };
    onMemberChange(params);
  };

  const handleImageChange = field => value => {
    setImageTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prevErrors => ({ ...prevErrors, [field]: false }));
    setState(prevState => ({ ...prevState, [field]: value }));
  };

  const cropSquare = { cropAspect: 1 };

  return (
    <Form
      className="m-form m-form--fit m-form--label-align-right"
      id={formId}
      submitMethod={handleSubmit}
      validationRules={validationRules}
    >
      {submitHandler => (
        <>
          <div className="m-portlet__body">
            <div className="row profile-skipStep">
              <div className={`${!onSkipStep ? 'col-12' : 'col-6'}`}>
                <FormSubtitle title={t('profile_tab_moods')} />
              </div>
              {onSkipStep && (
                <div className="col-6 skip-wrapper btn-wrapper">
                  <SkipStepButton onClick={onSkipStep} />
                </div>
              )}
            </div>
            <div className="row">
              <div className="col-10 offset-2 row">
                <div className="col-12 col-lg-4">
                  <label
                    htmlFor="unhappy"
                    className={errors?.unhappy ? 'label-error' : ''}
                  >
                    {isFieldRequiredLabel(
                      requiredFieldsPerBlock,
                      PROFILE_BLOCK.MOODS,
                      'unhappy',
                      t('profile_unhappy'),
                    )}
                  </label>
                  <ImageInput
                    id="unhappy"
                    inputId="unhappyInput"
                    name="unhappy"
                    initialValue={member.mood && member.mood.unhappy}
                    onChange={handleImageChange('unhappy')}
                    inputProps={cropSquare}
                    label=""
                    groupClassName=""
                    labelClassName=""
                    containerClassName=""
                  />
                  {errors?.unhappy && <ErrorMessage />}
                </div>
                <div className="col-12 col-lg-4">
                  <label
                    htmlFor="moodless"
                    className={errors?.moodless ? 'label-error' : ''}
                  >
                    {isFieldRequiredLabel(
                      requiredFieldsPerBlock,
                      PROFILE_BLOCK.MOODS,
                      'moodless',
                      t('profile_moodless'),
                    )}
                  </label>
                  <ImageInput
                    id="moodless"
                    inputId="moodlessInput"
                    name="moodless"
                    initialValue={member.mood && member.mood.moodless}
                    onChange={handleImageChange('moodless')}
                    inputProps={cropSquare}
                    label=""
                    groupClassName=""
                    labelClassName=""
                    containerClassName=""
                  />
                  {errors?.moodless && (
                    <ErrorMessage
                      styleMessage={{
                        marginLeft: '-21px',
                        marginTop: '10px',
                      }}
                    />
                  )}
                </div>
                <div className="col-12 col-lg-4">
                  <label
                    htmlFor="happy"
                    className={errors?.happy ? 'label-error' : ''}
                  >
                    {isFieldRequiredLabel(
                      requiredFieldsPerBlock,
                      PROFILE_BLOCK.MOODS,
                      'happy',
                      t('profile_happy'),
                    )}
                  </label>
                  <ImageInput
                    id="happy"
                    inputId="happyInput"
                    name="happy"
                    initialValue={member.mood && member.mood.happy}
                    onChange={handleImageChange('happy')}
                    inputProps={cropSquare}
                    label=""
                    groupClassName=""
                    labelClassName=""
                    containerClassName=""
                  />
                  {errors?.happy && <ErrorMessage />}
                </div>
              </div>
            </div>
          </div>
          <FormFooter submitHandler={submitHandler} />
        </>
      )}
    </Form>
  );
}

MoodsForm.propTypes = {
  /** translation function */
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  /** member being edited */
  member: PropTypes.shape(UserProp.propType).isRequired,
  /** member change event handler */
  onMemberChange: PropTypes.func.isRequired,
  onSkipStep: PropTypes.func,
};

MoodsForm.defaultProps = {
  onSkipStep: null,
};

export default compose(translate, withCSRF)(MoodsForm);
